import React, { useEffect, useRef, useState } from 'react';
import { pubblicoFetch, urlPubblica, ErrorePubblico } from './apiPubblica';
import { TemaPagina, useVariabiliTema } from './aspetto';
import { raggruppaSlotPerGiorno, GiornoSlot, SlotBase } from './slotGiorni';
import { oggiRoma, aggiungiGiorniIso } from '../utils/dateRoma';

export interface SedePubblica { nome: string; indirizzo: string; telefono: string | null; email: string | null; haLogo: boolean }

// Titolo della scheda del browser: finché non c'è (dati ancora da caricare) resta quello della pagina HTML.
export function useTitoloPagina(titolo: string | null) {
  useEffect(() => {
    if (titolo) document.title = titolo;
  }, [titolo]);
}

// `tema`: colori, logo e copertina del cantiere. I colori sono variabili CSS impostate sul contenitore.
// `anteprima`: dentro il back office la pagina non deve occupare tutta l'altezza dello schermo.
export function Contenitore({ children, tema = null, anteprima = false }: { children: React.ReactNode; tema?: TemaPagina | null; anteprima?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useVariabiliTema(ref, tema);
  return (
    <div className={`bop-pubblico${anteprima ? ' bop-pubblico--anteprima' : ''}`} ref={ref}>
      <div className="bop-pubblico__contenitore">
        {tema?.copertina && <img className="bop-pubblico__copertina" src={tema.copertina} alt="" />}
        {children}
      </div>
    </div>
  );
}

export function Intestazione({ sede, tokenPagina, logoImpresa = null, nomeImpresa }: {
  sede: SedePubblica | null; tokenPagina: string | null; logoImpresa?: string | null; nomeImpresa?: string;
}) {
  if (!sede && !logoImpresa) return null;
  return (
    <div className="bop-pubblico__intestazione">
      {sede?.haLogo && tokenPagina && <img className="bop-pubblico__logo" src={urlPubblica(`/api/pubblico/prenotazioni/${encodeURIComponent(tokenPagina)}/logo`)} alt="" />}
      {logoImpresa && <img className="bop-pubblico__logo" src={logoImpresa} alt={nomeImpresa ? `Logo ${nomeImpresa}` : ''} />}
      {sede && (
        <div className="bop-pubblico__sezione">
          <p className="bop-pubblico__negozio">{sede.nome}</p>
          {sede.indirizzo && <p className="bop-pubblico__indirizzo">{sede.indirizzo}</p>}
        </div>
      )}
    </div>
  );
}

export function Contatti({ sede }: { sede: SedePubblica | null }) {
  if (!sede || (!sede.telefono && !sede.email)) return null;
  return (
    <p className="bop-pubblico__testo">
      Per qualsiasi necessità: {sede.telefono && <a className="bop-pubblico__link" href={`tel:${sede.telefono.replace(/\s/g, '')}`}>{sede.telefono}</a>}
      {sede.telefono && sede.email ? ' · ' : ''}
      {sede.email && <a className="bop-pubblico__link" href={`mailto:${sede.email}`}>{sede.email}</a>}
    </p>
  );
}

export function PaginaNonDisponibile() {
  useTitoloPagina('Pagina non disponibile');
  return (
    <Contenitore>
      <div className="bop-pubblico__card">
        <h1 className="bop-pubblico__titolo">Pagina non disponibile</h1>
        <p className="bop-pubblico__testo">Il link non è valido o è scaduto. Controlla di averlo copiato per intero oppure contatta il negozio.</p>
      </div>
    </Contenitore>
  );
}

// Dominio prenota.* aperto senza il link del cantiere (es. digitato a mano).
export function PaginaIngresso() {
  useTitoloPagina('Prenota il tuo appuntamento');
  return (
    <Contenitore>
      <div className="bop-pubblico__card">
        <h1 className="bop-pubblico__titolo">Prenota il tuo appuntamento</h1>
        <p className="bop-pubblico__testo">Per prenotare apri il link o inquadra il QR code che hai ricevuto per il tuo cantiere. Se non li trovi, contatta il negozio.</p>
      </div>
    </Contenitore>
  );
}

// Scelta di giorno e ora: carica 14 giorni per volta dall'endpoint indicato.
export function SceltaSlot({ percorsoSlot, valore, onScegli, versione }: {
  percorsoSlot: string; valore: string | null; onScegli: (inizio: string) => void; versione: number;
}) {
  const oggi = oggiRoma();
  const [da, setDa] = useState(oggi);
  const [giorni, setGiorni] = useState<GiornoSlot<SlotBase>[] | null>(null);
  const [giornoAttivo, setGiornoAttivo] = useState<string | null>(null);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    // "Date successive/precedenti" si possono premere in fretta: una risposta vecchia non deve
    // sovrascrivere quella della finestra più recente.
    let obsoleta = false;
    setGiorni(null);
    setErrore('');
    // @api GET /api/pubblico/prenotazioni/:token/slot?da -> {"success":true,"data":"array"}
    pubblicoFetch<{ data: SlotBase[] }>(`${percorsoSlot}?da=${da}`)
      .then(r => {
        if (obsoleta) return;
        const g = raggruppaSlotPerGiorno(r.data);
        setGiorni(g);
        // Dopo un ricaricamento (es. slot appena occupato) si resta sul giorno in vista, se ha ancora orari.
        setGiornoAttivo(prec => (g.some(x => x.giorno === prec) ? prec : (g[0]?.giorno ?? null)));
      })
      .catch(e => {
        if (!obsoleta) setErrore((e as ErrorePubblico).message);
      });
    return () => { obsoleta = true; };
  }, [percorsoSlot, da, versione]);

  const attivo = giorni?.find(g => g.giorno === giornoAttivo);
  return (
    <div className="bop-pubblico__card">
      <h2 className="bop-pubblico__titolo">Scegli giorno e ora</h2>
      <p className="bop-pubblico__testo">L'incontro dura circa un'ora.</p>
      {errore && <p className="bop-pubblico__errore" role="alert">{errore}</p>}
      {!giorni && !errore && <p className="bop-pubblico__testo">Cerco gli orari liberi…</p>}
      {giorni && giorni.length === 0 && <p className="bop-pubblico__testo">Nessun orario libero in queste due settimane.</p>}
      {giorni && giorni.length > 0 && (
        <>
          <div className="bop-pubblico__giorni">
            {giorni.map(g => (
              <button type="button" key={g.giorno} className={`bop-pubblico__giorno ${g.giorno === giornoAttivo ? 'bop-pubblico__giorno--attivo' : ''}`} aria-pressed={g.giorno === giornoAttivo} onClick={() => setGiornoAttivo(g.giorno)}>
                {g.etichetta}
              </button>
            ))}
          </div>
          <div className="bop-pubblico__orari">
            {attivo?.slot.map(s => (
              <button type="button" key={s.inizio} className={`bop-pubblico__orario ${s.inizio === valore ? 'bop-pubblico__orario--attivo' : ''}`} aria-pressed={s.inizio === valore} onClick={() => onScegli(s.inizio)}>
                {s.ora}
              </button>
            ))}
          </div>
        </>
      )}
      <div className="bop-pubblico__azioni">
        {da > oggi && <button type="button" className="bop-btn bop-btn-secondary" onClick={() => setDa(aggiungiGiorniIso(da, -14) < oggi ? oggi : aggiungiGiorniIso(da, -14))}>Date precedenti</button>}
        <button type="button" className="bop-btn bop-btn-secondary" onClick={() => setDa(aggiungiGiorniIso(da, 14))}>Date successive</button>
      </div>
    </div>
  );
}
